import React from 'react'
import PropTypes from 'prop-types'

import moment from 'moment'
import { TimeUtils } from '@fibo-app/fibo-collab-shared'

import TimeAllocationRow from './TimeAllocationRow'
import TimeAllocationTableHeader from './TimeAllocationTableHeader'
import TimeAllocationTableSummary from './TimeAllocationTableSummary'
import JobsSummary from './JobsSummary'
import TimeFormatter from 'infra/TimeFormatter'
import ModulesUtils from '../../infra/ModulesUtils'
import JobType from './JobType'

export default class TimeAllocationTable extends React.PureComponent {

  static propTypes = {
    startDateStr: PropTypes.string.isRequired,
    endDateStr: PropTypes.string.isRequired,
    tableTarget: PropTypes.oneOf([ 'worker', 'laborers' ]).isRequired,
    workerId: PropTypes.string,
    teamMembers: PropTypes.array.isRequired,
    jobs: PropTypes.object,
    timesheets: PropTypes.object,
    hideEmptyRows: PropTypes.bool.isRequired
  }

  filterIn = job => {
    // Check this job is assigned to the current worker
    // or this is the laborers table.
    if (this.props.tableTarget === 'worker') {
      return job.workersList.hasOwnProperty(this.props.workerId)
    }
    // All laborers are assigned to the job
    if (this.props.tableTarget === 'laborers') {
      return true
    }
    return false
  }

  getTime = (lineItem, laborersList, dayAllocations) => {
    if (!dayAllocations) {
      return 0
    }
    const itemAllocations = dayAllocations[lineItem.id]
    if (!itemAllocations) {
      return 0
    }
    // If the table target is for an specific worker,
    // then return the time allocated by that worker
    if (this.props.tableTarget === 'worker') {
      const currentWorkerAllocation = itemAllocations[this.props.workerId]
      if (currentWorkerAllocation) {
        return currentWorkerAllocation.timeInMinutes || 0
      }
    }
    // If the table target is for the laborers, then aggregate
    // all the time allocated by all the laborers in this job
    if (this.props.tableTarget === 'laborers') {
      let total = 0
      for (const laborerId in laborersList) {
        const laborerAllocation = itemAllocations[laborerId]
        if (laborerAllocation) {
          total += laborerAllocation.timeInMinutes || 0
        }
      }
      return total
    }
    return 0
  }

  getTimes = (lineItem, laborersList, jobAllocations, installJobsTotals) => {
    let totalTime = 0
    const timeList = []
    if (jobAllocations) {
      const cursor = moment(this.props.startDateStr)
      let index = 0
      while (cursor.isSameOrBefore(this.props.endDateStr)) {
        const dateStr = cursor.format('YYYY-MM-DD')
        const time = this.getTime(lineItem, laborersList, jobAllocations[dateStr])
        timeList.push(time)
        installJobsTotals[index++] += time
        totalTime += time
        cursor.add(1, 'days')
      }
    }
    return {
      times: timeList,
      totalTime
    }
  }

  getPayrollTime = dateStr => {
    const workerTimesheets = this.props.timesheets[this.props.workerId]
    if (workerTimesheets) {
      const dayTimesheet = workerTimesheets[dateStr]
      if (dayTimesheet) {
        const clockingDay =
          (dayTimesheet.info &&
            dayTimesheet.info.modules &&
            dayTimesheet.info.modules.clockingDay) ||
          {}
        return TimeUtils.getIntervalTimeFromClocking(clockingDay)
      }
    }
    return 0
  }

  computeInstallationValues = () => {
    const lineItemsJobs = []
    const daysSpan = this.getDaysSpan(this.props.startDateStr, this.props.endDateStr)
    const installJobsTotals = new Array(daysSpan + 1).fill(0)
    for (const jobKey in this.props.jobs) {
      // Filter jobs assigned to this worker
      const job = this.props.jobs[jobKey]
      // Check is a line item job
      if (this.filterIn(job) && job.type === JobType.Install) {
        if (job.lineItems && job.lineItems.list) {
          for (const lineItemId in job.lineItems.list) {
            const lineItem = job.lineItems.list[lineItemId]
            if (lineItem && !lineItem.deleted) {
              const time = this.getTimes(lineItem, job.laborersList, job.lineItems.allocation, installJobsTotals)
              time.jobName = job.name
              time.lineItem = lineItem.name
              time.key = lineItem.id
              // Hide Empty rows if this is an empty row and that setting is enabled
              if (time.totalTime !== 0 || !this.props.hideEmptyRows) {
                lineItemsJobs.push(time)
              }
            }
          }
        }
      }
    }
    const payrollInfo = []
    const cursor = moment(this.props.startDateStr)
    while (cursor.isSameOrBefore(this.props.endDateStr)) {
      const dateStr = cursor.format('YYYY-MM-DD')
      payrollInfo.push(this.getPayrollTime(dateStr))
      cursor.add(1, 'days')
    }
    return { lineItemsJobs, payrollInfo, installJobsTotals }
  }

  /**
   * Returns the number of days between two dates, given by their string representation.
   */
  getDaysSpan = (startDateStr, endDateStr) => {
    return Math.abs(moment(endDateStr).diff(moment(startDateStr), 'days'))
  }

  aggregateTimeFromSharedClockings = (job, serviceJobsTotals, maintenanceJobsTotals) => {
    for (const dateStr in job.sharedClockings) {
      // Aggregate time for all workers as it's a shared clocking definition.
      const dayClockings = job.sharedClockings[dateStr]
      const time = TimeUtils.getIntervalTimeFromEvent(dayClockings)
      const index = this.getDaysSpan(this.props.startDateStr, dateStr)
      if (index >= 0 && index < serviceJobsTotals.length) {
        if (job.type === JobType.Service) {
          serviceJobsTotals[index] += time
        }
        else {
          maintenanceJobsTotals[index] += time
        }
      }
    }
  }

  aggregateTimeFromWorkerClockings = (job, serviceJobsTotals, maintenanceJobsTotals) => {
    for (const dateStr in job.workerClockings) {
      const dayClockings = job.workerClockings[dateStr]
      for (const workerId in dayClockings) {
        // Only aggregate values from the current worker.
        if (workerId === this.props.workerId) {
          const workerClockings = dayClockings[workerId]
          const time = TimeUtils.getIntervalTimeFromEvent(workerClockings)
          const index = this.getDaysSpan(this.props.startDateStr, dateStr)
          if (index >= 0 && index < serviceJobsTotals.length) {
            if (job.type === JobType.Service) {
              serviceJobsTotals[index] += time
            } else {
              maintenanceJobsTotals[index] += time
            }
          }
        }
      }
    }
  }

  computeServiceMaintenanceValues = () => {
    const daysSpan = this.getDaysSpan(this.props.startDateStr, this.props.endDateStr)
    const serviceJobsTotals = new Array(daysSpan + 1).fill(0)
    const maintenanceJobsTotals = new Array(daysSpan + 1).fill(0)
    for (const jobKey in this.props.jobs) {
      // Filter jobs assigned to this worker
      const job = this.props.jobs[jobKey]
      if (this.filterIn(job)
        && (job.type === JobType.Service || job.type === JobType.Maintenance)) {
        // Handle shared clockings and worker clockings computation
        if (job.sharedClockings) {
          this.aggregateTimeFromSharedClockings(job, serviceJobsTotals, maintenanceJobsTotals)
        }
        else
        if (job.workerClockings) {
          this.aggregateTimeFromWorkerClockings(job, serviceJobsTotals, maintenanceJobsTotals)
        }
      }
    }
    return { serviceJobsTotals, maintenanceJobsTotals }
  }

  computeSummaryValues = (lineItemsJobs, serviceValues, maintenanceValues) => {
    const dayTotals = new Array(serviceValues.length).fill(0)
    for (let i = 0; i < lineItemsJobs.length; i++) {
      for (let k = 0; k < lineItemsJobs[i].times.length; k++) {
        dayTotals[k] += lineItemsJobs[i].times[k]
      }
    }
    for (const k in serviceValues) {
      dayTotals[k] += serviceValues[k]
    }
    for (const k in maintenanceValues) {
      dayTotals[k] += maintenanceValues[k]
    }
    return dayTotals
  }

  renderTitle = (dayTotals, payrollInfo) => {
    const dayTotal =
      dayTotals.length === 0 ? 0 : dayTotals.reduce((a, b) => a + b)
    const payrollTotal =
      payrollInfo.length === 0 ? 0 : payrollInfo.reduce((a, b) => a + b)
    const ratioTotal = payrollTotal === 0 ? 0 : 100 * dayTotal / payrollTotal
    return (
      <div
        style={{ display: 'flex', flexDirection: 'row', marginTop: 12, marginBottom: 12 }}>
        <span style={{ fontSize: 16, marginRight: 4 }}>
          { this.props.tableTarget === 'worker'
            ? ModulesUtils.getMemberName(this.props.teamMembers, this.props.workerId)
            : 'Laborers'
          }
        </span>
        <span style={{ fontSize: 16, color: '#595b5f' }}>
          {` – ${TimeFormatter.toHoursMinutesFormat(payrollTotal) || '0h'} worked, ${TimeFormatter.toHoursMinutesFormat(dayTotal) || '0h'} allocated, ${ratioTotal.toFixed(1)}%`}
        </span>
      </div>
    )
  }

  getTableData = () => {
    return {
      name: this.props.tableTarget === 'worker'
        ? ModulesUtils.getMemberName(this.props.teamMembers, this.props.workerId)
        : 'Laborers',
      startDateStr: this.props.startDateStr,
      totalDays: 1 + this.getDaysSpan(this.props.startDateStr, this.props.endDateStr),
      ...this.installationValues,
      ...this.serviceMaintenanceValues,
      dayTotals: this.dayTotals
    }
  }

  render() {
    this.installationValues = this.computeInstallationValues()
    this.serviceMaintenanceValues = this.computeServiceMaintenanceValues()
    const { lineItemsJobs, payrollInfo, installJobsTotals } = this.installationValues
    const {
      serviceJobsTotals,
      maintenanceJobsTotals
    } = this.serviceMaintenanceValues
    this.dayTotals = this.computeSummaryValues(
      lineItemsJobs,
      serviceJobsTotals,
      maintenanceJobsTotals
    )
    return (
      <div style={styles.container}>
        {this.renderTitle(this.dayTotals, payrollInfo)}

        <TimeAllocationTableHeader
          startDateStr={this.props.startDateStr}
          endDateStr={this.props.endDateStr} />

        {lineItemsJobs.map(lineItemJob => (
          <TimeAllocationRow
            key={lineItemJob.jobName + '-' + lineItemJob.lineItem}
            lineItemJob={lineItemJob} />
        ))}
        <JobsSummary values={ installJobsTotals } title="Install Jobs Total" />        
        <JobsSummary values={ serviceJobsTotals } title="Service Jobs Total" />
        <JobsSummary values={ maintenanceJobsTotals } title="Recurrent Jobs Total" />
        <TimeAllocationTableSummary
          dayTotals={this.dayTotals}
          payrollInfo={payrollInfo} />
      </div>
    )
  }
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    marginTop: 20,
    marginBottom: 20,
    marginLeft: 8,
    marginRight: 8 
  }
}




// WEBPACK FOOTER //
// ./src/reports/timeAllocation/TimeAllocationTable.js